export type PortfolioHoldingSortMode = 'largest' | 'movers';

export type PortfolioHoldingPriceStatus = 'current' | 'close' | 'reported' | 'unavailable';

export interface PortfolioHoldingLike {
  id: string;
  symbol: string | null;
  name: string;
  securityType: string | null;
  quantity: number;
  value: number | null;
  allocationPercent: number | null;
  priceStatus: PortfolioHoldingPriceStatus;
  costBasisAmount: number | null;
  gainLossAmount: number | null;
  dayChangeAmount: number | null;
  dayChangePercent: number | null;
}

export interface PortfolioHoldingSymbolAggregate<T extends PortfolioHoldingLike = PortfolioHoldingLike> {
  key: string;
  symbol: string | null;
  name: string;
  totalValue: number | null;
  totalQuantity: number;
  totalCostBasis: number | null;
  totalGainLoss: number | null;
  gainLossPercent: number | null;
  dayChangeAmount: number | null;
  dayChangePercent: number | null;
  allocationPercent: number | null;
  positions: T[];
  priceStatus: T['priceStatus'];
  representative: T;
}

const EXCHANGE_TRADED_SECURITY_TYPES = new Set(['equity', 'etf']);
const DAILY_MARKET_SECURITY_TYPES = new Set(['equity', 'etf', 'cryptocurrency']);

function rankNullableValue(value: number | null): number {
  return value ?? Number.NEGATIVE_INFINITY;
}

function rankNullableAbsoluteValue(value: number | null): number {
  return value == null ? Number.NEGATIVE_INFINITY : Math.abs(value);
}

function compareRankDescending(a: number, b: number): number {
  if (a === b) return 0;
  return b - a;
}

function compareHoldingNames(a: { name: string; symbol: string | null }, b: { name: string; symbol: string | null }) {
  return (a.symbol ?? a.name).localeCompare(b.symbol ?? b.name);
}

function addNullable(total: number | null, value: number | null): number | null {
  return value == null ? total : (total ?? 0) + value;
}

function resolvePriceStatus<T extends PortfolioHoldingLike>(positions: readonly T[]): T['priceStatus'] {
  const first = positions[0];
  const fallback = first?.priceStatus ?? 'unavailable';
  if (positions.some((position) => position.priceStatus === 'unavailable')) return 'unavailable' as T['priceStatus'];
  if (positions.some((position) => position.priceStatus === 'reported')) return 'reported' as T['priceStatus'];
  if (positions.some((position) => position.priceStatus === 'close')) return 'close' as T['priceStatus'];
  return fallback;
}

export function filterExchangeTradedHoldings<T extends PortfolioHoldingLike>(items: readonly T[]): T[] {
  return items.filter((item) => item.securityType != null && EXCHANGE_TRADED_SECURITY_TYPES.has(item.securityType));
}

export function filterDailyMarketHoldings<T extends PortfolioHoldingLike>(items: readonly T[]): T[] {
  return items.filter(
    (item) =>
      item.securityType != null &&
      DAILY_MARKET_SECURITY_TYPES.has(item.securityType) &&
      item.priceStatus === 'current' &&
      item.dayChangeAmount != null,
  );
}

export function sortPortfolioHoldings<T extends PortfolioHoldingLike>(
  items: readonly T[],
  sort: PortfolioHoldingSortMode,
): T[] {
  const next = [...items];
  if (sort === 'movers') {
    return next.sort((a, b) => {
      const moveDiff = compareRankDescending(
        rankNullableAbsoluteValue(a.dayChangeAmount),
        rankNullableAbsoluteValue(b.dayChangeAmount),
      );
      if (moveDiff !== 0) return moveDiff;
      const valueDiff = compareRankDescending(rankNullableValue(a.value), rankNullableValue(b.value));
      if (valueDiff !== 0) return valueDiff;
      return compareHoldingNames(a, b);
    });
  }

  return next.sort((a, b) => {
    const valueDiff = compareRankDescending(rankNullableValue(a.value), rankNullableValue(b.value));
    if (valueDiff !== 0) return valueDiff;
    return compareHoldingNames(a, b);
  });
}

export function aggregatePortfolioHoldingsBySymbol<T extends PortfolioHoldingLike>(
  items: readonly T[],
): PortfolioHoldingSymbolAggregate<T>[] {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = item.symbol ?? `__${item.id}`;
    const list = map.get(key);
    if (list) {
      list.push(item);
    } else {
      map.set(key, [item]);
    }
  }

  const aggregates: PortfolioHoldingSymbolAggregate<T>[] = [];
  for (const [key, positions] of map) {
    const representative = positions[0];
    if (!representative) continue;

    const totalValue = positions.reduce((acc, position) => addNullable(acc, position.value), null as number | null);
    const totalCostBasis = positions.reduce(
      (acc, position) => addNullable(acc, position.costBasisAmount),
      null as number | null,
    );
    const totalGainLoss = positions.reduce(
      (acc, position) => addNullable(acc, position.gainLossAmount),
      null as number | null,
    );
    const dayChangeAmount = positions.reduce(
      (acc, position) => addNullable(acc, position.dayChangeAmount),
      null as number | null,
    );
    const allocationPercent = positions.reduce(
      (acc, position) => addNullable(acc, position.allocationPercent),
      null as number | null,
    );
    const totalQuantity = positions.reduce((acc, position) => acc + position.quantity, 0);
    const priorValue = totalValue != null && dayChangeAmount != null ? totalValue - dayChangeAmount : null;
    const dayChangePercent =
      priorValue != null && priorValue !== 0 && dayChangeAmount != null ? dayChangeAmount / priorValue : null;
    const gainLossPercent =
      totalCostBasis != null && totalCostBasis !== 0 && totalGainLoss != null ? totalGainLoss / totalCostBasis : null;

    aggregates.push({
      key,
      symbol: representative.symbol,
      name: representative.name,
      totalValue,
      totalQuantity,
      totalCostBasis,
      totalGainLoss,
      gainLossPercent,
      dayChangeAmount,
      dayChangePercent,
      allocationPercent,
      positions,
      priceStatus: resolvePriceStatus(positions),
      representative,
    });
  }

  return sortPortfolioHoldingAggregates(aggregates, 'largest');
}

export function sortPortfolioHoldingAggregates<T extends PortfolioHoldingLike>(
  items: readonly PortfolioHoldingSymbolAggregate<T>[],
  sort: PortfolioHoldingSortMode,
): PortfolioHoldingSymbolAggregate<T>[] {
  const next = [...items];
  if (sort === 'movers') {
    return next.sort((a, b) => {
      const moveDiff = compareRankDescending(
        rankNullableAbsoluteValue(a.dayChangeAmount),
        rankNullableAbsoluteValue(b.dayChangeAmount),
      );
      if (moveDiff !== 0) return moveDiff;
      const valueDiff = compareRankDescending(rankNullableValue(a.totalValue), rankNullableValue(b.totalValue));
      if (valueDiff !== 0) return valueDiff;
      return compareHoldingNames(a, b);
    });
  }

  return next.sort((a, b) => {
    const valueDiff = compareRankDescending(rankNullableValue(a.totalValue), rankNullableValue(b.totalValue));
    if (valueDiff !== 0) return valueDiff;
    return compareHoldingNames(a, b);
  });
}
